import { AUTH_BASE_URL } from '../config/api';

/**
 * Native OpenAI Realtime API integration
 * Uses WebSocket connection for true real-time audio streaming
 */
export class NativeRealtimeAudioManager {
  constructor() {
    this.ws = null;
    this.mediaRecorder = null;
    this.audioContext = null;
    this.stream = null;
    this.isConnected = false;
    this.isRecording = false;
    this.sessionId = null;
    this.conversation = [];
    
    // Audio processing
    this.audioQueue = [];
    this.isPlayingAudio = false;
    this.currentAudio = null;
    
    // Callbacks
    this.onMessageCallback = null;
    this.onErrorCallback = null;
    this.onStatusCallback = null;
    this.onAudioStartCallback = null;
    this.onAudioEndCallback = null;
    
    // Settings
    this.selectedModel = 'gpt-4o-realtime-preview-2024-12-17';
    this.selectedVoice = 'alloy';
    
    // Response timeout handling
    this.responseTimeout = null;
    this.speechStartTime = null;
    
    // Continuous recording for hands-free mode
    this.isContinuousRecording = false;
  }

  setInitialConversation(conversation) {
    this.conversation = [...conversation];
    
    // Send existing conversation to Realtime API if connected
    if (this.isConnected && conversation.length > 0) {
      this.sendExistingConversation(conversation);
    }
  }

  async tryDirectPCMCapture() {
    // Try to use AudioWorklet for direct PCM16 capture (future optimization)
    try {
      if (!window.AudioWorklet) {
        console.log('AudioWorklet not supported');
        return false;
      }

      // For now, return false to use the fallback method
      // This can be implemented later for even better audio quality
      return false;
    } catch (error) {
      console.log('Direct PCM capture not available:', error);
      return false;
    }
  }

  setModel(model) {
    this.selectedModel = model;
  }

  setVoice(voice) {
    this.selectedVoice = voice;
    
    // Update session voice if connected
    if (this.isConnected) {
      this.updateSessionVoice(voice);
    }
  }

  async initialize(onMessage, onError, onStatusChange, onAudioStart, onAudioEnd) {
    this.onMessageCallback = onMessage;
    this.onErrorCallback = onError;
    this.onStatusCallback = onStatusChange;
    this.onAudioStartCallback = onAudioStart;
    this.onAudioEndCallback = onAudioEnd;

    try {
      // Generate unique session ID
      this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Initialize audio context and microphone
      await this.initializeAudio();
      
      // Connect to WebSocket
      await this.connectWebSocket();
      
      this.onStatusCallback?.('ready');
      return true;
    } catch (error) {
      console.error('Failed to initialize native realtime audio:', error);
      this.onErrorCallback?.(error);
      return false;
    }
  }

  async initializeAudio() {
    // Request microphone access with optimal settings for OpenAI Realtime API
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: { ideal: 24000, min: 16000, max: 48000 }, // Prefer 24kHz
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        latency: 0.01, // Low latency for real-time
        volume: 1.0
      }
    });

    console.log('Audio stream constraints applied:', this.stream.getAudioTracks()[0].getSettings());

    // Create audio context with 24kHz sample rate
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 24000,
      latencyHint: 'interactive'
    });

    // Resume audio context if suspended
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    console.log('Audio context initialized:', {
      sampleRate: this.audioContext.sampleRate,
      state: this.audioContext.state,
      baseLatency: this.audioContext.baseLatency
    });
  }

  async connectWebSocket() {
    return new Promise((resolve, reject) => {
      const wsUrl = `${AUTH_BASE_URL.replace('http', 'ws')}/api/realtime-audio/${this.sessionId}`;
      console.log('Connecting to WebSocket:', wsUrl);
      console.log('Session ID:', this.sessionId);
      
      // Connection timeout for better error handling
      const connectionTimeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 10000); // 10 second timeout
      
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('Connected to Realtime WebSocket');
        this.isConnected = true;
        this.setupEventHandlers();
        resolve();
      };

      this.ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('WebSocket connection error:', error);
        this.isConnected = false;
        reject(new Error('Failed to connect to voice service'));
      };

      this.ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket connection closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        this.isConnected = false;
        
        // Notify status change
        this.onStatusCallback?.('disconnected');
      };
    });
  }

  setupEventHandlers() {
    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleRealtimeEvent(data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };
  }

  handleRealtimeEvent(event) {
    console.log('Realtime event:', event.type);

    switch (event.type) {
      case 'session.created':
        console.log('Session created:', event.session.id);
        break;

      case 'session.updated':
        console.log('Session updated');
        break;

      case 'input_audio_buffer.speech_started':
        console.log('Speech started detected by server VAD');
        this.speechStartTime = Date.now();
        this.onStatusCallback?.('listening');
        
        // Set a timeout to manually trigger response if speech_stopped doesn't come
        if (this.responseTimeout) {
          clearTimeout(this.responseTimeout);
        }
        this.responseTimeout = setTimeout(() => {
          console.log('No speech_stopped detected, manually triggering response');
          this.triggerResponse();
        }, 5000); // 5 second timeout
        break;

      case 'input_audio_buffer.speech_stopped':
        console.log('Speech stopped detected by server VAD');
        if (this.responseTimeout) {
          clearTimeout(this.responseTimeout);
          this.responseTimeout = null;
        }
        this.onStatusCallback?.('processing');
        break;

      case 'conversation.item.created':
        console.log('Conversation item created:', event.item.type);
        this.handleConversationItem(event.item);
        break;

      case 'response.created':
        console.log('Response created');
        this.onStatusCallback?.('thinking');
        break;

      case 'response.output_item.added':
        console.log('Response item added');
        break;

      case 'response.content_part.added':
        console.log('Content part added:', event.part.type);
        break;

      case 'response.audio.delta':
        console.log('Audio delta received, length:', event.delta?.length || 0);
        this.handleAudioDelta(event.delta);
        break;

      case 'response.audio.done':
        console.log('Audio response complete');
        this.finalizeAudioResponse();
        break;

      case 'response.done':
        console.log('Response complete');
        this.onStatusCallback?.('ready');
        break;

      case 'error':
        console.error('Realtime API error:', event.error);
        this.onErrorCallback?.(new Error(event.error.message));
        break;

      default:
        console.log('Unhandled event type:', event.type);
    }
  }

  handleConversationItem(item) {
    if (item.type === 'message') {
      const message = {
        role: item.role,
        content: this.extractTextContent(item.content),
        timestamp: Date.now(),
        audio: null,
        isRealtime: true
      };

      this.conversation.push(message);
      this.onMessageCallback?.(message);
    }
  }

  extractTextContent(content) {
    if (!content || !Array.isArray(content)) return '';
    
    return content
      .filter(part => part.type === 'text')
      .map(part => part.text)
      .join(' ');
  }

  handleAudioDelta(delta) {
    if (!delta) return;

    // Queue audio delta for playback
    this.audioQueue.push(delta);
    
    // Start playing if not already playing
    if (!this.isPlayingAudio) {
      this.isPlayingAudio = true;
      this.onStatusCallback?.('playing');
      this.onAudioStartCallback?.();
      this.playNextAudioChunk();
    }
  }

  async playNextAudioChunk() {
    if (this.audioQueue.length === 0) {
      this.isPlayingAudio = false;
      this.onAudioEndCallback?.();
      return;
    }

    const audioData = this.audioQueue.shift();
    
    try {
      // Convert base64 PCM16 to audio buffer
      const audioBuffer = this.base64ToAudioBuffer(audioData);
      const audioBufferSource = this.audioContext.createBufferSource();
      audioBufferSource.buffer = audioBuffer;
      audioBufferSource.connect(this.audioContext.destination);
      
      audioBufferSource.onended = () => {
        // Play next chunk
        this.playNextAudioChunk();
      };

      audioBufferSource.start();
      this.currentAudio = audioBufferSource;
    } catch (error) {
      console.error('Failed to play audio chunk:', error);
      this.playNextAudioChunk(); // Continue with next chunk
    }
  }

  base64ToAudioBuffer(base64Data) {
    const binaryString = window.atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Convert PCM16 to Float32Array
    const pcm16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
      float32[i] = pcm16[i] / 32768.0;
    }

    // Create AudioBuffer
    const audioBuffer = this.audioContext.createBuffer(1, float32.length, 24000);
    audioBuffer.copyToChannel(float32, 0);
    
    return audioBuffer;
  }

  finalizeAudioResponse() {
    // Audio response is complete
    console.log('Audio response finalized');
  }

  triggerResponse() {
    if (!this.isConnected) return;
    
    console.log('Manually triggering response generation');
    
    // Send a response creation request
    const event = {
      type: 'response.create',
      response: {
        modalities: ['text', 'audio'],
        instructions: 'Please respond to the user input.'
      }
    };
    
    this.ws.send(JSON.stringify(event));
  }

  async startRecording() {
    if (!this.isConnected || this.isRecording) return;

    try {
      this.isRecording = true;
      this.onStatusCallback?.('recording');

      // Try to use direct PCM capture via AudioWorklet for best compatibility
      if (await this.tryDirectPCMCapture()) {
        console.log('Using direct PCM capture for optimal audio quality');
        return; // Direct PCM capture handles recording differently
      }
      
      // Fallback to MediaRecorder with compatible formats
      const options = {};
      
      // Prioritize formats that work best with OpenAI Realtime API
      if (MediaRecorder.isTypeSupported('audio/wav')) {
        options.mimeType = 'audio/wav';
        console.log('Using WAV format - best for audio quality');
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=pcm')) {
        options.mimeType = 'audio/webm;codecs=pcm';
        console.log('Using WebM PCM format');
      } else {
        // Use server-side conversion for problematic formats
        console.log('Browser audio format limitations - using server conversion');
        options.mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 
          'audio/webm;codecs=opus' : 'audio/webm';
        options.useServerConversion = true;
      }
      
      console.log('MediaRecorder using format:', options.mimeType || 'default');
      this.mediaRecorder = new MediaRecorder(this.stream, options);
      
      // Collect audio chunks instead of sending immediately
      this.audioChunks = [];
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('Audio chunk received:', event.data.size, 'bytes');
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = async () => {
        if (this.audioChunks.length > 0) {
          console.log('Recording stopped, processing', this.audioChunks.length, 'chunks');
          const audioBlob = new Blob(this.audioChunks, { type: options.mimeType || 'audio/webm' });
          await this.sendAudioChunk(audioBlob);
          this.audioChunks = [];
        }
      };

      // Start recording without time slicing to get complete audio
      this.mediaRecorder.start();
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      this.onErrorCallback?.(error);
    }
  }

  stopRecording() {
    if (!this.isRecording || this.isContinuousRecording) return;

    this.isRecording = false;
    
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }

    this.onStatusCallback?.('processing');
  }

  async startContinuousRecording() {
    if (this.isContinuousRecording || !this.isConnected) return;
    
    try {
      this.isContinuousRecording = true;
      console.log('Starting continuous recording for hands-free mode');
      
      // Create MediaRecorder for continuous streaming with compatible formats
      const options = {};
      
      // Prioritize formats that Web Audio API can decode
      if (MediaRecorder.isTypeSupported('audio/wav')) {
        options.mimeType = 'audio/wav';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=pcm')) {
        options.mimeType = 'audio/webm;codecs=pcm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=flac')) {
        options.mimeType = 'audio/ogg;codecs=flac';
      } else {
        // Use server-side conversion for unsupported formats
        console.log('Using server-side audio conversion for continuous recording');
        options.mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 
          'audio/webm;codecs=opus' : 'audio/webm';
        options.useServerConversion = true;
      }
      
      this.mediaRecorder = new MediaRecorder(this.stream, options);
      
      // Send audio chunks in real-time for continuous mode
      this.mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0 && this.isConnected && this.isContinuousRecording) {
          await this.sendAudioChunk(event.data);
        }
      };

      // Start recording with small chunks for real-time streaming
      this.mediaRecorder.start(100); // 100ms chunks for real-time streaming
      this.onStatusCallback?.('ready');
      
    } catch (error) {
      console.error('Failed to start continuous recording:', error);
      this.onErrorCallback?.(error);
    }
  }

  stopContinuousRecording() {
    if (!this.isContinuousRecording) return;
    
    this.isContinuousRecording = false;
    
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    
    console.log('Stopped continuous recording');
  }

  async sendAudioChunk(audioBlob) {
    try {
      // Convert audio blob to PCM16 format expected by OpenAI
      const base64Audio = await this.convertAudioToPCM16(audioBlob);
      
      // Skip if conversion failed or returned null
      if (!base64Audio) {
        console.log('Skipping audio chunk - conversion returned null');
        return;
      }
      
      // Send audio to Realtime API
      const event = {
        type: 'input_audio_buffer.append',
        audio: base64Audio
      };

      console.log('Sending audio chunk to OpenAI:', {
        type: event.type,
        audioLength: base64Audio.length
      });

      this.ws.send(JSON.stringify(event));
    } catch (error) {
      console.error('Failed to send audio chunk:', error);
    }
  }

  async convertAudioToPCM16(audioBlob) {
    try {
      console.log('Converting audio blob:', {
        size: audioBlob.size,
        type: audioBlob.type
      });
      
      // Skip empty or very small audio blobs
      if (!audioBlob || audioBlob.size < 100) {
        console.log('Skipping empty or very small audio blob');
        return null;
      }
      
      // Try client-side conversion first
      try {
        return await this.convertAudioClientSide(audioBlob);
      } catch (clientError) {
        console.log('Client-side conversion failed, trying server-side:', clientError.message);
        return await this.convertAudioServerSide(audioBlob);
      }
    } catch (error) {
      console.error('All audio conversion methods failed:', error);
      return null;
    }
  }

  async convertAudioClientSide(audioBlob) {
    // Decode the audio blob using Web Audio API
    const arrayBuffer = await audioBlob.arrayBuffer();
    console.log('Audio array buffer size:', arrayBuffer.byteLength);
    
    // Skip empty buffers
    if (arrayBuffer.byteLength === 0) {
      throw new Error('Empty audio buffer');
    }
    
    const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
    console.log('Decoded audio buffer:', {
      sampleRate: audioBuffer.sampleRate,
      numberOfChannels: audioBuffer.numberOfChannels,
      duration: audioBuffer.duration,
      length: audioBuffer.length
    });
    
    // Skip very short audio (less than 100ms)
    if (audioBuffer.duration < 0.1) {
      throw new Error('Audio too short');
    }
    
    // Target format: 24kHz mono PCM16
    const targetSampleRate = 24000;
    const targetChannels = 1;
    
    // Always resample to ensure correct format
    const targetLength = Math.ceil(audioBuffer.duration * targetSampleRate);
    const offlineContext = new OfflineAudioContext(targetChannels, targetLength, targetSampleRate);
    
    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;
    
    // Convert to mono if needed
    if (audioBuffer.numberOfChannels > 1) {
      const merger = offlineContext.createChannelMerger(1);
      const splitter = offlineContext.createChannelSplitter(audioBuffer.numberOfChannels);
      const gain = offlineContext.createGain();
      gain.gain.value = 1.0 / audioBuffer.numberOfChannels; // Average channels
      
      source.connect(splitter);
      for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
        splitter.connect(gain, i);
      }
      gain.connect(merger, 0, 0);
      merger.connect(offlineContext.destination);
    } else {
      source.connect(offlineContext.destination);
    }
    
    source.start();
    const resampledBuffer = await offlineContext.startRendering();
    const audioData = resampledBuffer.getChannelData(0);
    
    // Convert float32 to PCM16 with proper clamping
    const pcm16Array = new Int16Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
      // Clamp to [-1, 1] range and convert to 16-bit signed integer
      const sample = Math.max(-1, Math.min(1, audioData[i]));
      pcm16Array[i] = Math.round(sample * 32767);
    }
    
    console.log('PCM16 conversion complete:', {
      samples: pcm16Array.length,
      bytes: pcm16Array.byteLength,
      sampleRate: targetSampleRate,
      duration: pcm16Array.length / targetSampleRate
    });
    
    // Convert Int16Array to base64 string
    const uint8Array = new Uint8Array(pcm16Array.buffer);
    const base64 = this.arrayBufferToBase64(uint8Array.buffer);
    
    console.log('Base64 audio length:', base64.length);
    
    // Validate base64 is not empty
    if (!base64 || base64.length === 0) {
      throw new Error('Generated empty base64 audio');
    }
    
    return base64;
  }

  async convertAudioServerSide(audioBlob) {
    console.log('Converting audio via server...');
    
    // Send audio to server for conversion
    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.webm');
    
    const response = await fetch(`${AUTH_BASE_URL}/api/audio/convert-to-pcm16`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`Server conversion failed: ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.base64Audio) {
      throw new Error('Server returned empty audio data');
    }
    
    console.log('Server conversion successful, base64 length:', result.base64Audio.length);
    return result.base64Audio;
  }

  // Helper method for base64 conversion
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    let binary = '';
    
    // Convert in chunks to avoid call stack size exceeded
    const chunkSize = 8192;
    for (let i = 0; i < len; i += chunkSize) {
      const chunk = bytes.subarray(i, Math.min(i + chunkSize, len));
      binary += String.fromCharCode.apply(null, chunk);
    }
    
    return btoa(binary);
  }

  // Remove this fallback method as it doesn't produce valid PCM16

  sendExistingConversation(conversation) {
    // Send existing conversation items to establish context
    conversation.forEach((msg, index) => {
      const event = {
        type: 'conversation.item.create',
        item: {
          id: `item_${index}`,
          type: 'message',
          role: msg.role,
          content: [
            {
              type: 'input_text',
              text: msg.content
            }
          ]
        }
      };
      
      this.ws.send(JSON.stringify(event));
    });
  }

  updateSessionVoice(voice) {
    const event = {
      type: 'session.update',
      session: {
        voice: voice
      }
    };
    
    this.ws.send(JSON.stringify(event));
  }

  cleanup() {
    this.stopRecording();
    
    if (this.currentAudio) {
      try {
        this.currentAudio.stop();
      } catch (error) {
        // Ignore errors when stopping audio
      }
    }
    
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close();
    }
    
    this.isConnected = false;
    this.isRecording = false;
    this.isPlayingAudio = false;
  }

  getConversation() {
    return this.conversation;
  }

  clearConversation() {
    this.conversation = [];
    
    // Clear conversation on server
    if (this.isConnected) {
      const event = {
        type: 'conversation.item.truncate',
        item_id: 'item_0'
      };
      this.ws.send(JSON.stringify(event));
    }
  }
}

// Export singleton instance
export const nativeRealtimeAudioManager = new NativeRealtimeAudioManager();